import { Dispatch } from '@reduxjs/toolkit';
import { resettingAppData } from '../redux/slices';
import { showError } from '../redux/slices/error';
import { toast } from 'react-toastify';

/**
 * @heading1: Error Handler
 * @danger: Handles errors received from an API response and manages related state or notifications.
 * @heading3: Parameters
 * @ol:
 * error : {string} - The error message to be displayed in a popup.
 * dispatch : {Dispatch} - The Redux dispatch function for triggering state updates.
 * rejectWithValue : {(value: string) => void} - A callback to reject the promise with a specific error value.
 * @heading3: Details
 * @ul:
 * If the error message indicates a session issue (e.g., Unauthorized access or session expiration), the app data will be reset, and a "Logged out" error message will be displayed.
 * For any other errors, a generic error popup is shown with the provided error message.
 * The function ensures that the error message is returned as the rejected value for further handling.
 * @code:
 * errorHandler('Unauthorized', dispatch, rejectWithValue);
 */
export const errorHandler = async (error: string, dispatch: Dispatch, rejectWithValue: (value: string) => void) => {
    // Default to a general error message if none is provided
    const errorMessage = error || 'An unexpected error occurred';

    // Check if the error relates to session issues
    if (
        ['Unauthorized', 'Your session has expired', 'Access Denied'].includes(errorMessage)
    ) {
        try {
            // Reset app data and show session expiry message
            await dispatch(resettingAppData());
            setTimeout(() => {
                dispatch(showError({
                    title: '',
                    description: 'You have been Logged out'
                }));
            }, 500);
        } catch (e) {
            // Catch any errors during session reset
        }
    } else {
        toast.error(errorMessage, {
            position: 'top-left',
            theme: 'dark',
            autoClose: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            closeButton: true,
            toastId: Date.now()
        });
        // dispatch(showError({
        //     title: 'Error',
        //     description: errorMessage
        // }));
    }
    // Reject the promise and return the error message for further handling
    return rejectWithValue(errorMessage);
};
